import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import selectors from 'state/selectors';
import service from 'services';
import utils from 'utils';
import TextArea from 'components/Input/Textarea';
import Table, { TableColumn } from 'components/Table';
import Modal from 'components/Modal';
import actions from 'state/actions';
import { useDispatch } from 'react-redux';
import { OpenReject } from 'models/response';
import DropDown from 'components/Input/dropdown/Dropdown';
import styled from 'styled-components/macro';
import { useAsync } from 'react-use';
import { useHistory } from 'react-router';
import { fontWeightPalette, fontSizePalette } from 'utils/styleConstants';

const Section = styled.div`
  margin-bottom: 30px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
`;
const Label = styled.p`
  font-family: ${({ theme }) => theme.fontFamily};
  font-size: ${fontSizePalette.medium};
  font-weight: ${fontWeightPalette.medium};
  letter-spacing: -0.5px;
  line-height: 19px;
  margin-right: 8px;
`;
const Title = styled.h4`
  margin-bottom: 8px;
`;

const RejectDocument = () => {
  const t = utils.intl.useTranslator();
  const dispatch = useDispatch();
  const protocol = useSelector(selectors.documents.selectActiveDocumentID);
  const [openRejectResponse, setOpenRejectResponse] = useState<OpenReject>();
  const [note, setNote] = useState<string>('');
  const [idWfCausal, setIdWfCausal] = useState<number>();
  const history = useHistory();
  const { dateFormat, timeZone } = useSelector(selectors.user.getUserPreference);

  useAsync(async () => {
    try {
      if (protocol) {
        const { data } = await service.openReject(protocol);
        setOpenRejectResponse(data);
      }
    } catch (err) {
      console.error(err);
    }
  }, []);

  const storicoColumns: TableColumn[] = [
    {
      accessor: 'causalCode',
      Header: t('causalCode'),
    },
    { accessor: 'note', Header: t('note') },
    { accessor: 'protocol', Header: t('protocol') },
    {
      accessor: 'rejectionReason',
      Header: t('rejectionReason'),
      Cell: ({ value }: { value: null | string }) => value ?? '',
    },
    {
      accessor: 'sendDate',
      Header: t('sendDate'),
      Cell: ({ value }: { value: number }) => utils.date.convertDate(value, timeZone ?? 'UTC'),
      filter: utils.date.epochFilterFunction(timeZone ?? 'UTC'),
      filterType: 'date',
      dateFormat,
    },
    {
      accessor: 'type',
      Header: t('type'),
    },
    {
      accessor: 'typeDescription',
      Header: t('typeDescription'),
    },
  ];

  const onNoteChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setNote(e.target.value);
  };

  const onCausalChange = (option: { value: number; label: string }) => {
    setIdWfCausal(option.value);
  };

  const handleConfirm = async () => {
    try {
      if (protocol && idWfCausal) {
        const request = {
          protocol,
          note,
          idWfCausal,
        };
        await service.closeReject(request);
        dispatch(actions.modal.closeModal());
        dispatch(actions.documents.removeRows([protocol]));
        if (utils.documents.isOpenDocumentView()) dispatch(actions.documents.openNextDocument(history));
        utils.app.notify('success', t('document successfully rejected'));
      }
    } catch (err) {
      console.error(err);
    }
  };

  const causalOptions =
    openRejectResponse?.wfCausalRejects.map(({ idWfCausal, causalName }) => ({
      value: idWfCausal,
      label: causalName,
    })) || [];

  const confirmButtonActive = Boolean(
    (!openRejectResponse?.noteMandatory || note.length > 1) && (!causalOptions.length || idWfCausal),
  );

  const storicoRows =
    openRejectResponse?.wfHistory.map((historyRecord) => ({
      causalCode: historyRecord.causalCode,
      note: historyRecord.note,
      protocol: historyRecord.protocol,
      rejectionReason: historyRecord.rejectionReason,
      sendDate: historyRecord.sendDate,
      type: historyRecord.type,
      typeDescription: historyRecord.typeDescription,
    })) || [];
  return (
    <>
      <Modal.Header title={t('reject')} subtitle={t('reject-desc')} />
      <Modal.Content>
        {causalOptions.length ? (
          <Section>
            <Label>{t('causale')}: </Label>
            <DropDown id="causal-dropdown" onChange={onCausalChange} name="causale" options={causalOptions} />
          </Section>
        ) : null}
        {storicoRows.length ? (
          <>
            <Title>{t('storico')}</Title>
            <Table
              id="reject-storico-table"
              hasPagination
              hasSort
              hasFilter
              columns={storicoColumns}
              rows={storicoRows}
            />
          </>
        ) : null}
        {openRejectResponse?.name ? (
          <Section>
            <Label>{t('destinatario')}:</Label>
            <p style={{ padding: '0.1em' }}>{openRejectResponse.name}</p>
          </Section>
        ) : null}
        <>
          <Title>{t('note')}</Title>
          <TextArea name="Nota" placeholder={t('note here')} onChange={onNoteChange} minHeight="62px" />
        </>
      </Modal.Content>
      <Modal.Footer confirmText={t('continue')} confirmAction={handleConfirm} confirmDisabled={!confirmButtonActive} />
    </>
  );
};

export default RejectDocument;
